// Team-news widgets — types + pure shaping (Phase 3).
//
// Covers the "availability / news" family of widgets (Injuries & Suspension
// first; Doubtful Players + News & Sentiment follow). Unlike the earlier
// converted widgets, the dummy data is NOT embedded here: it lives once in the
// legacy `data.js` (`window.BentoData.*`) and is read through a typed accessor
// in app-web (`src/data/legacyData.ts`). This file holds only the shared TYPES
// (the shape app-api must eventually satisfy) and pure shaping helpers — no data
// and no DOM/window access, so it stays framework-free and unit-testable.

/** Availability status shown on an injury/suspension badge. */
export type InjuryStatus = "OUT" | "SUSP";

/** One player row in the Injuries & Suspension widget. */
export interface InjuryPlayer {
  name: string;
  status: InjuryStatus;
  /** Reason (e.g. "Knee", "Personal") — not currently displayed, kept for the contract. */
  detail: string;
  /** Badge brand colour, hex (e.g. "#e74c3c"). */
  colour: string;
}

/** Per-team injury payload: a last-updated stamp + the affected players. */
export interface TeamInjuryData {
  /** Last-updated time, e.g. "09:42" or "—" when unknown. */
  updated: string;
  players: InjuryPlayer[];
}

/** Injuries keyed by full team name (e.g. "Arsenal"). */
export type InjuryData = Record<string, TeamInjuryData>;

/** Empty payload used when a team has no injury data (matches the legacy fallback). */
export const EMPTY_INJURY: TeamInjuryData = { updated: "—", players: [] };

/**
 * Order players for display: suspensions first, then everyone else, otherwise
 * keeping input order. Mirrors the legacy comparator exactly
 * (`(a==='SUSP'?0:1) - (b==='SUSP'?0:1)`) so the rendered order — and the
 * visual A/B — stays identical. Returns a new array; the input is not mutated.
 */
export function sortInjuryPlayers(players: readonly InjuryPlayer[]): InjuryPlayer[] {
  return players
    .slice()
    .sort(
      (a, b) => (a.status === "SUSP" ? 0 : 1) - (b.status === "SUSP" ? 0 : 1)
    );
}

// --- Doubtful Players ---------------------------------------------------------

/** One player in the Doubtful Players widget. Only `name` is displayed today;
 *  `detail`/`pct` are part of the contract for when app-api serves it. */
export interface DoubtfulPlayer {
  name: string;
  detail: string;
  /** Estimated chance of playing, 0–100. */
  pct: number;
}

/** Per-team doubtful payload: a last-updated stamp + the doubtful players. */
export interface TeamDoubtfulData {
  updated: string;
  players: DoubtfulPlayer[];
}

/** Doubtful players keyed by full team name. */
export type DoubtfulData = Record<string, TeamDoubtfulData>;

/** Empty payload used when a team has no doubtful data (matches the legacy fallback). */
export const EMPTY_DOUBTFUL: TeamDoubtfulData = { updated: "—", players: [] };

// --- News & Sentiment ---------------------------------------------------------

/** One headline in the News & Sentiment widget. */
export interface NewsItem {
  text: string;
  /** Time-of-day stamp, e.g. "09:15". */
  time: string;
  /** Sentiment: true = positive (green bar), false = negative (red bar). */
  pos: boolean;
}

/** Per-team news payload. */
export interface TeamNewsData {
  items: NewsItem[];
}

/** News keyed by full team name. */
export type NewsData = Record<string, TeamNewsData>;

/** Empty payload used when a team has no news (matches the legacy fallback). */
export const EMPTY_NEWS: TeamNewsData = { items: [] };

/** Sentiment bar colour: green when positive, red when negative (legacy hexes). */
export function newsBarColor(pos: boolean): string {
  return pos ? "#2ecc71" : "#e74c3c";
}
